/**
 * ECharts bar chart renderer for the generic widget system.
 *
 * Supports:
 *  - Simple bar (one metric, grouped by dimension)
 *  - Grouped/stacked bar (multiple metrics or series_by dimension)
 *  - Click-to-drill-down via onDrillDown callback
 *  - Horizontal layout for categorical comparisons (entities)
 *  - Vertical layout for time-series
 */
import ReactECharts from 'echarts-for-react'
import type { PerfRow, MetricKey, GroupByDimension } from './types'
import { METRIC_LABELS, formatMetric, HIERARCHY_DIMENSIONS, getDimensionLabel } from './types'
import type { BenchmarkDataSeries } from './useBenchmarkQueries'
import { makeChartReady } from './echarts-theme'

interface Props {
  data: PerfRow[]
  groupBy: GroupByDimension
  metrics: MetricKey[]
  seriesBy?: GroupByDimension
  onDrillDown?: (code: string, label: string) => void
  groupComparisons?: BenchmarkDataSeries[]
  primaryLabel?: string
  /** Goal-line overlay (Phase D2). */
  goalLine?: {
    value: number
    label: string
    direction?: 'higher_is_better' | 'lower_is_better'
  }
  /** ECharts group key for cross-chart hover/zoom sync (Phase F1). */
  chartGroup?: string
}

const getLabel = getDimensionLabel

/** Get the code value for drill-down targeting */
function getCode(row: PerfRow, dim: GroupByDimension): string {
  if (HIERARCHY_DIMENSIONS.has(dim) && row.entity_code) return String(row.entity_code)
  return String(row[dim] ?? '')
}

const COLORS = [
  '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#14b8a6', '#f97316', '#6366f1',
]

/** Round a value up to a clean axis maximum (1/2/5 × 10ⁿ). */
function niceCeil(v: number): number {
  if (!isFinite(v) || v <= 0) return 1
  const mag = Math.pow(10, Math.floor(Math.log10(v)))
  const norm = v / mag
  const nice = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10
  return nice * mag
}

/**
 * Value axis for a horizontal bar chart — mirrored on top and bottom so the
 * scale stays readable next to a long list of bars. Both axes share an
 * explicit 0..max range (a second value axis can't auto-sync otherwise) and
 * format ticks via the metric's own formatter (percent → "20%", $ → "$1M").
 */
function horizontalValueAxes(metric: MetricKey, max: number) {
  const axisLabel = { fontSize: 10, formatter: (v: number) => formatMetric(metric, v) }
  return [
    { type: 'value' as const, min: 0, max, axisLabel },
    { type: 'value' as const, min: 0, max, position: 'top' as const, splitLine: { show: false }, axisLabel },
  ]
}

/** Max value across every series' data, for a deterministic shared axis range. */
function seriesValueMax(series: { data: unknown[] }[]): number {
  const values = series
    .flatMap((s) => s.data)
    .filter((v): v is number => typeof v === 'number' && isFinite(v))
  return niceCeil(Math.max(0, ...values))
}

/** Compute a weighted average for rate metrics or simple average for counts from comparison rows. */
function computeComparisonAverage(rows: PerfRow[], metric: MetricKey): number | null {
  if (rows.length === 0) return null
  let sum = 0
  let count = 0
  for (const row of rows) {
    const val = row[metric]
    if (typeof val === 'number' && !isNaN(val)) {
      sum += val
      count++
    }
  }
  return count > 0 ? sum / count : null
}

export default function BarChartRenderer({ data, groupBy, metrics, seriesBy, onDrillDown, groupComparisons, primaryLabel, goalLine, chartGroup }: Props) {
  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-gray-400">
        No data
      </div>
    )
  }

  const isTimeSeries = groupBy === 'fiscal_year' || groupBy === 'fiscal_quarter'
  const isHorizontal = !isTimeSeries && data.length > 5

  // If seriesBy is set (and no group comparisons), use the grouped bar path
  if (seriesBy && (!groupComparisons || groupComparisons.length === 0)) {
    return <GroupedBarChart data={data} groupBy={groupBy} metric={metrics[0]} seriesBy={seriesBy} isHorizontal={isHorizontal} onDrillDown={onDrillDown} chartGroup={chartGroup} />
  }

  // Simple bar: one or more metrics
  const categories = data.map((r) => getLabel(r, groupBy))
  const codes = data.map((r) => getCode(r, groupBy))

  const hasComparisons = groupComparisons && groupComparisons.some((c) => c.data.length > 0)
  const metric = metrics[0]

  const series: any[] = metrics.map((m, i) => ({
    name: hasComparisons ? (primaryLabel || 'Primary') : METRIC_LABELS[m],
    type: 'bar' as const,
    data: data.map((r) => {
      const val = r[m]
      return typeof val === 'number' ? val : parseFloat(String(val)) || 0
    }),
    itemStyle: { color: COLORS[i % COLORS.length], borderRadius: [2, 2, 0, 0] },
    barMaxWidth: 40,
  }))

  // Append comparison data
  if (hasComparisons) {
    for (const comp of groupComparisons!) {
      if (comp.data.length === 0) continue

      // Check if comparison data shares the same dimension as primary
      const firstRow = comp.data[0]
      const compMatchesPrimary = firstRow && (getLabel(firstRow, groupBy) !== 'Unknown') &&
        categories.some((cat) => comp.data.some((r) => getLabel(r, groupBy) === cat))

      if (compMatchesPrimary) {
        // Same dimension — show as grouped bars
        const compMap = new Map<string, number>()
        for (const row of comp.data) {
          const label = getLabel(row, groupBy)
          const val = row[metric]
          compMap.set(label, typeof val === 'number' ? val : parseFloat(String(val)) || 0)
        }
        series.push({
          name: comp.source.label,
          type: 'bar' as const,
          data: categories.map((cat) => compMap.get(cat) ?? 0),
          itemStyle: {
            color: comp.source.color || '#8b5cf6',
            borderRadius: [2, 2, 0, 0],
            opacity: 0.6,
          },
          barMaxWidth: 30,
        })
      } else {
        // Different dimension (comparison is aggregated by time, primary is by entity)
        // Compute weighted average across all comparison rows and show as reference line
        const avgValue = computeComparisonAverage(comp.data, metric)
        if (avgValue !== null) {
          // Add markLine to the first series
          if (!series[0].markLine) {
            series[0].markLine = { data: [], symbol: 'none', silent: true }
          }
          series[0].markLine.data.push({
            yAxis: isHorizontal ? undefined : avgValue,
            xAxis: isHorizontal ? avgValue : undefined,
            lineStyle: { type: 'dashed', color: comp.source.color || '#8b5cf6', width: 1.5 },
            label: {
              formatter: `${comp.source.label}: ${formatMetric(metric, avgValue)}`,
              fontSize: 10,
              color: comp.source.color || '#8b5cf6',
              position: 'insideEndTop',
            },
          })
        }
      }
    }
  }

  // Goal-line overlay (D2): horizontal markLine at the target value.
  if (goalLine && series.length > 0) {
    if (!series[0].markLine) {
      series[0].markLine = { data: [], symbol: 'none', silent: true }
    }
    series[0].markLine.data.push({
      yAxis: isHorizontal ? undefined : goalLine.value,
      xAxis: isHorizontal ? goalLine.value : undefined,
      lineStyle: { type: 'solid', color: '#f59e0b', width: 2 },
      label: {
        formatter: goalLine.label,
        fontSize: 10,
        color: '#f59e0b',
        position: 'insideEndTop',
      },
    })
  }

  const showLegend = hasComparisons || metrics.length > 1
  const valueMax = seriesValueMax(series)
  const valueFormatter = (v: number) => formatMetric(metric, v)

  const option: any = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any) => {
        const items = Array.isArray(params) ? params : [params]
        const header = items[0]?.axisValue || ''
        const lines = items.map((p: any) =>
          `${p.marker} ${p.seriesName}: <b>${formatMetric(metric, p.value)}</b>`,
        )
        return `<b>${header}</b><br/>${lines.join('<br/>')}`
      },
    },
    grid: {
      left: isHorizontal ? '30%' : 50,
      right: 20,
      // Horizontal charts carry a mirrored top axis — reserve a row for it.
      top: isHorizontal ? (showLegend ? 46 : 24) : (showLegend ? 40 : 15),
      bottom: isHorizontal ? 30 : 40,
      containLabel: false,
    },
    ...(showLegend ? {
      legend: {
        top: 0,
        textStyle: { fontSize: 11 },
      },
    } : {}),
    ...(isHorizontal
      ? {
          xAxis: horizontalValueAxes(metric, valueMax),
          yAxis: {
            type: 'category',
            data: categories,
            axisLabel: { fontSize: 10, width: 120, overflow: 'truncate' },
            inverse: true,
          },
        }
      : {
          xAxis: {
            type: 'category',
            data: categories,
            axisLabel: { fontSize: 10, rotate: categories.length > 8 ? 45 : 0 },
          },
          yAxis: { type: 'value', axisLabel: { fontSize: 10, formatter: valueFormatter } },
        }),
    series,
  }

  const onEvents = onDrillDown
    ? {
        click: (params: any) => {
          const idx = params.dataIndex
          if (idx !== undefined && codes[idx]) {
            onDrillDown(codes[idx], categories[idx])
          }
        },
      }
    : undefined

  return (
    <ReactECharts
      option={option}
      style={{ height: '100%', width: '100%' }}
      opts={{ renderer: 'canvas' }}
      onEvents={onEvents}
      onChartReady={makeChartReady(chartGroup)}
      key="primary"
    />
  )
}

/** Grouped bar chart when seriesBy is set */
function GroupedBarChart({
  data, groupBy, metric, seriesBy, isHorizontal, onDrillDown, chartGroup,
}: {
  data: PerfRow[]
  groupBy: GroupByDimension
  metric: MetricKey
  seriesBy: GroupByDimension
  isHorizontal: boolean
  onDrillDown?: (code: string, label: string) => void
  chartGroup?: string
}) {
  // Pivot data: { [groupLabel]: { [seriesLabel]: value } }
  const groupLabels: string[] = []
  const groupCodes: string[] = []
  const seriesLabels = new Set<string>()
  const pivoted = new Map<string, Map<string, number>>()

  for (const row of data) {
    const gLabel = getLabel(row, groupBy)
    const gCode = getCode(row, groupBy)
    const sLabel = getLabel(row, seriesBy)
    seriesLabels.add(sLabel)

    if (!pivoted.has(gLabel)) {
      pivoted.set(gLabel, new Map())
      groupLabels.push(gLabel)
      groupCodes.push(gCode)
    }
    const val = typeof row[metric] === 'number' ? (row[metric] as number) : parseFloat(String(row[metric])) || 0
    pivoted.get(gLabel)!.set(sLabel, val)
  }

  const seriesArr = Array.from(seriesLabels)
  const series = seriesArr.map((sLabel, i) => ({
    name: sLabel,
    type: 'bar' as const,
    data: groupLabels.map((gLabel) => pivoted.get(gLabel)?.get(sLabel) ?? 0),
    itemStyle: { color: COLORS[i % COLORS.length], borderRadius: [2, 2, 0, 0] },
    barMaxWidth: 30,
  }))

  const valueMax = seriesValueMax(series)
  const valueFormatter = (v: number) => formatMetric(metric, v)

  const option: any = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any) => {
        const items = Array.isArray(params) ? params : [params]
        const header = items[0]?.axisValue || ''
        const lines = items.map((p: any) =>
          `${p.marker} ${p.seriesName}: <b>${formatMetric(metric, p.value)}</b>`,
        )
        return `<b>${header}</b><br/>${lines.join('<br/>')}`
      },
    },
    // Scrollable single-row legend — a grouped series can have many entries
    // (e.g. every MAJCOM), which as a plain legend wraps over the plot.
    legend: { type: 'scroll', top: 0, textStyle: { fontSize: 11 } },
    // Horizontal charts carry a mirrored top axis below the legend.
    grid: { left: isHorizontal ? '30%' : 50, right: 20, top: isHorizontal ? 62 : 40, bottom: isHorizontal ? 30 : 40 },
    ...(isHorizontal
      ? {
          xAxis: horizontalValueAxes(metric, valueMax),
          yAxis: { type: 'category', data: groupLabels, axisLabel: { fontSize: 10, width: 120, overflow: 'truncate' }, inverse: true },
        }
      : {
          xAxis: { type: 'category', data: groupLabels, axisLabel: { fontSize: 10, rotate: groupLabels.length > 8 ? 45 : 0 } },
          yAxis: { type: 'value', axisLabel: { fontSize: 10, formatter: valueFormatter } },
        }),
    series,
  }

  const onEvents = onDrillDown
    ? {
        click: (params: any) => {
          const idx = params.dataIndex
          if (idx !== undefined && groupCodes[idx]) {
            onDrillDown(groupCodes[idx], groupLabels[idx])
          }
        },
      }
    : undefined

  return (
    <ReactECharts
      option={option}
      style={{ height: '100%', width: '100%' }}
      opts={{ renderer: 'canvas' }}
      onEvents={onEvents}
      onChartReady={makeChartReady(chartGroup)}
    />
  )
}
